/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantRequestId = 0;

      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.cbCrossAvailability();
      this.cbBindHandNone();
      this.cbMarkSelf();
      this.cbSizeFilter();
      this.cbSliderArrows();
      this.cbHandMemory();
      this.cbHandEscape();
      this.cbRewindOnChange();
      this.cbHandJump();
      this.updateAvailability();
    }

    /**
     * CheapBats 2026-09-13 (Mike): throwing hand is personal and essentially never changes —
     * almost nobody throws with both arms. So the shopper picks Hand once and it sticks for the
     * rest of the visit (and the next one), across every glove page, until they pick the other one.
     * Stored canonically as R / L so it still matches a listing that words the option differently
     * ("Right-Handed-Thrower" vs "Right Hand Throw"). Never auto-selects a hand that is out of
     * stock on the page being viewed — that would look like we were pushing them at a dead end.
     */
    cbHandMemory() {
      const sel = this.querySelector('.option-selector[data-option="Hand"]');
      if (!sel || sel.dataset.cbHandMem) return;
      sel.dataset.cbHandMem = '1';
      const KEY = 'cb_hand';
      const side = (v) => (v || '').trim().charAt(0).toUpperCase();      // R | L
      sel.addEventListener('change', () => {
        const on = sel.querySelector('.js-option:checked');
        if (!on) return;
        try { localStorage.setItem(KEY, side(on.value)); } catch (e) {}
      });
      if (sel.querySelector('.js-option:checked')) return;               // page already decided
      let saved = null;
      try { saved = localStorage.getItem(KEY); } catch (e) {}
      if (saved !== 'R' && saved !== 'L') return;
      const hit = [...sel.querySelectorAll('.js-option')].find((i) =>
        side(i.value) === saved && !i.disabled && !i.classList.contains('is-unavailable'));
      if (!hit || hit.checked) return;
      hit.checked = true;
      hit.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * CheapBats 2026-09-01: when the option the shopper just changed makes another
     * already-selected value impossible (e.g. Size 26 picked, then a colour that has no 26),
     * deselect that other value instead of leaving the new choice looking greyed/sold out.
     * The shopper's latest click always wins; radio pickers only (dropdowns untouched).
     */
    cbPruneConflicts(changedSelector) {
      const dataEl = this.querySelector('[data-cb-variants]');
      if (!dataEl || !changedSelector) return;
      let variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      const selectors = Array.from(this.optionSelectors);
      const changedIndex = selectors.indexOf(changedSelector);
      if (changedIndex === -1) return;
      const selected = this.getSelectedOptions();
      if (selected[changedIndex] === null) return;
      let pruned = false;
      selectors.forEach((selector, j) => {
        if (j === changedIndex || selected[j] === null) return;
        if (selector.dataset.selectorType === 'dropdown') return;
        const ok = variants.some((v) => v.a
          && v.o[changedIndex] === selected[changedIndex] && v.o[j] === selected[j]);
        if (ok) return;
        const input = selector.querySelector('input:checked');
        if (input) { input.checked = false; selected[j] = null; pruned = true; }
      });
      if (pruned) {
        this.updateStatusClasses();
        this.updateAddToCartButton();
      }
    }

    /**
     * CheapBats 2026-09-01: with only SOME options chosen (e.g. a colour but no size yet) the
     * theme leaves the gallery alone because there is no variant. Show the image of the first
     * variant that matches what IS selected (in-stock first) so picking a colour swaps the photo.
     */
    cbSwapMediaForPartial() {
      const selected = this.getSelectedOptions();
      if (!selected.includes(null) || selected.every((s) => s === null)) return;
      const dataEl = this.querySelector('[data-cb-variants]');
      if (!dataEl) return;
      let variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      const match = (v) => v.m && v.o.every((o, j) => selected[j] === null || o === selected[j]);
      const v = variants.find((x) => x.a && match(x)) || variants.find(match);
      if (!v) return;
      const gallery = this.section.querySelector('media-gallery');
      const item = gallery && gallery.viewer && gallery.viewer.querySelector(`[data-media-id="${v.m}"]`);
      if (item && typeof gallery.customSetActiveMedia === 'function') gallery.customSetActiveMedia(item, true);
    }

    /**
     * CheapBats 2026-09-01: cross-option availability. Shopify only marks a value
     * unavailable relative to EARLIER options (Color -> Size), so picking a size never
     * greys out colours. This re-derives is-unavailable for every option value against
     * the full current selection using the per-variant JSON emitted by
     * snippets/variant-picker.liquid ([data-cb-variants]). No-op if that JSON is absent.
     */
    cbCrossAvailability() {
      const dataEl = this.querySelector('[data-cb-variants]');
      if (!dataEl) return;
      let variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      const selected = this.getSelectedOptions();
      this.optionSelectors.forEach((selector, i) => {
        selector.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const val = input.dataset.value !== undefined ? input.dataset.value : input.value;
          if (val === '') return;
          const ok = variants.some((v) => v.a && v.o[i] === val
            && v.o.every((o, j) => j === i || selected[j] === null || o === selected[j]));
          if (input.dataset.productUrl) {
            // combined-listing links (each = another product): judge by the selected Hand against the
            // sibling's in-stock hands (data-cb-hands from the nightly snapshot) — works on parent AND
            // child pages. No Hand picked → keep the server's landing state. 2026-09-13
            const handSel = this.querySelector('.option-selector[data-option="Hand"] .js-option:checked');
            if (input.dataset.cbHands === undefined) return;   // no snapshot row — leave it alone
            const hands = input.dataset.cbHands.split('|').filter(Boolean);
            if (!hands.length) { input.classList.add('is-unavailable'); return; }   // sold out everywhere
            if (!handSel) { input.classList.remove('is-unavailable'); return; }     // no Hand picked yet
            input.classList.toggle('is-unavailable', !hands.includes(handSel.value));
            return;
          }
          input.classList.toggle('is-unavailable', !ok);
          this.cbRefreshPillPrice(input, i, val, variants, selected);
        });
      });
      this.setAttribute('data-cb-cross', '');
      this.cbExtraAvailability();
      this.cbReorderUnavailable();
      if (typeof this.cbMarkHandFilter === 'function') this.cbMarkHandFilter();
      this.cbHandOnlyTags();
    }

    /**
     * CheapBats 2026-09-13 (Mike): picking a hand THIS glove isn't made in used to leave the chip
     * selected AND struck through — a contradiction, and nothing you can buy. Every style is its own
     * product, so the honest answer to "I throw left" is to take the shopper to a glove that is built
     * that way: same size where possible, otherwise the first sibling in that hand (the grid is
     * already sorted buyable-first). If nothing in the series has it, leave the page alone —
     * cbHandEscape's greyed chip covers that case.
     */
    cbHandJump() {
      // Mike 9/13: picking a hand must SHOW the gloves made in it, not navigate away. The grid
      // already sorts them to the front; all that's left is to stop the chip looking struck through,
      // because here it is a filter the shopper chose, not a dead end.
      if (this.dataset.cbHandJump) return;
      this.dataset.cbHandJump = '1';
      const side = (v) => (v || '').trim().charAt(0).toUpperCase();
      const mark = () => {
        const sel = this.querySelector('.option-selector[data-option="Hand"]');
        const grid = this.querySelector('.cb-swatch-grid');
        if (!sel || !grid) return;
        sel.querySelectorAll('.js-option').forEach((i) => {
          const want = side(i.value);
          let n = 0, low = null;
          grid.querySelectorAll('[data-cb-hands]').forEach((el) => {
            if (!(el.dataset.cbHands || '').split('|').some((x) => side(x) === want)) return;
            n += 1;
            const card = el.classList.contains('cb-extra-swatch') ? el : el.nextElementSibling;
            const p = card && card.querySelector ? card.querySelector('.cb-swatch-price') : null;
            const v = p ? parseFloat(p.textContent.replace(/[^\d.]/g, '')) : NaN;
            if (!isNaN(v) && (low === null || v < low)) low = v;
          });
          // Struck ONLY when nothing in the whole series comes in that hand. If the series has it,
          // the chip is a live choice (it carries "From $x / N styles") — and it must look the same
          // whether or not it happens to be selected.
          i.classList.toggle('cb-hand-filter', i.classList.contains('is-unavailable') && n > 0);
          // Mike 9/13: a hand this glove isn't made in shows what the series DOES have in it —
          // the price on such a chip is unbuyable here, so the count takes its place.
          const label = i.nextElementSibling;
          if (!label || !label.classList.contains('opt-label')) return;
          // keep the count even when this chip is the selected one — the theme prints no price for a
          // hand the product doesn't carry, which left the chip blank
          // Mike 9/13: both hands always carry their style count, not just the unavailable one
          const show = n > 0;
          let c = label.querySelector('.cb-hand-count');
          label.classList.toggle('cb-has-count', show);
          // only a hand this product can't sell gives up its price line for the count
          label.classList.toggle('cb-count-only', show && i.classList.contains('is-unavailable'));
          if (!show) { if (c) c.remove(); return; }
          if (!c) {
            c = document.createElement('span');
            c.className = 'cb-hand-count';
            label.appendChild(c);
          }
          // cheapest glove the series has in that hand, in the slot a real chip uses for its price
          let f = label.querySelector('.cb-hand-from');
          if (low !== null && i.classList.contains('is-unavailable')) {
            if (!f) {
              f = document.createElement('span');
              f.className = 'cb-hand-from';
              label.insertBefore(f, c);
            }
            f.textContent = 'From $' + (low % 1 ? low.toFixed(2) : low.toFixed(2));
          } else if (f) { f.remove(); }
          c.textContent = n + (n === 1 ? ' style' : ' styles') + ' in stock';
        });
      };
      this.cbMarkHandFilter = mark;     // cbCrossAvailability re-runs this once it has recomputed
      mark();
    }

    /**
     * CheapBats 2026-09-13 (Mike): when a pick reshuffles the grid, rewind the slider. Changing Hand
     * re-sorts the buyable styles to the front — but the shopper is left looking at wherever they had
     * scrolled to, which is now all greyed, and the in-stock ones are off-screen behind them.
     */
    cbRewindOnChange() {
      if (this.dataset.cbRewind) return;
      this.dataset.cbRewind = '1';
      this.addEventListener('change', (e) => {
        const el = e.target;
        if (!el || !el.classList || !el.classList.contains('js-option')) return;
        // rewind twice: once now, once after the theme has finished its own re-render, which
        // otherwise restores the old scroll position a beat later
        const rewind = () => this.querySelectorAll('.cb-swatch-grid').forEach((g) => {
          g.scrollLeft = 0;
          g.dispatchEvent(new Event('scroll'));
        });
        setTimeout(rewind, 0);
        setTimeout(rewind, 250);
        setTimeout(rewind, 600);
      });
    }

    /**
     * CheapBats 2026-09-13 (Mike): a dead end to back out of. Some gloves are made in one hand only —
     * land on a lefty-only closeout as a righty and the page offers you nothing you can throw with.
     * So render the missing hand as a greyed, clickable chip next to the one that exists (same look
     * as the greyed hand chip on a series page) — it goes to the series, where that hand is stocked.
     */
    cbHandEscape() {
      const sel = this.querySelector('.option-selector[data-option="Hand"]');
      const grid = this.querySelector('.cb-swatch-grid');
      if (!sel || sel.dataset.cbEscape) return;
      const wrap = sel.querySelector('.option-selector__btns');
      const parent = grid && grid.dataset.cbParent;
      if (!wrap || !parent) return;
      const side = (v) => (v || '').trim().charAt(0).toUpperCase();
      const have = [...sel.querySelectorAll('.js-option')].map((i) => side(i.value));
      // Which hands does the rest of the series stock? (from the siblings' own snapshot data)
      const inSeries = new Set();
      (grid.dataset.cbSeriesHands || '').split('|').filter(Boolean).forEach((h) => inSeries.add(side(h)));
      if (!inSeries.size) {
        grid.querySelectorAll('[data-cb-hands]').forEach((el) => {
          (el.dataset.cbHands || '').split('|').filter(Boolean).forEach((h) => inSeries.add(side(h)));
        });
      }
      let want = null;
      try { want = side(localStorage.getItem('cb_hand') || ''); } catch (e) {}
      ['R', 'L'].forEach((h) => {
        if (have.includes(h)) return;                       // this glove is made in that hand
        // show it if the series stocks it, or (before siblings load) if it's the hand they want
        if (inSeries.size ? !inSeries.has(h) : h !== want) return;
        sel.dataset.cbEscape = '1';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'opt-label opt-label--btn btn relative text-center cb-hand-none';
        b.dataset.cbHandNone = h === 'L' ? 'Left-Handed-Thrower' : 'Right-Handed-Thrower';
        b.title = 'Not made in this hand — see the styles that are';
        b.addEventListener('click', () => { window.location.href = parent; });
        // Mike 9/13: the count lives INSIDE the chip, where the price sits on a real hand chip,
        // so the greyed option reads as an invitation rather than a wall.
        let n = 0;
        grid.querySelectorAll('[data-cb-hands]').forEach((el) => {
          if ((el.dataset.cbHands || '').split('|').some((x) => side(x) === h)) n += 1;
        });
        b.innerHTML = '<span>' + (h === 'L' ? 'Left Hand Throw' : 'Right Hand Throw') + '</span>'
          + (n ? '<span class="cb-hand-count">' + n + ' Model' + (n === 1 ? '' : 's') + ' In Stock</span>' : '');
        wrap.appendChild(b);
      });
    }

    /**
     * CheapBats 2026-09-14 (Mike): a style greyed because it isn't made in the selected hand says so
     * — "LHT Only" / "RHT Only" — the same way an off-size style says which size it is. Without it a
     * greyed glove just looks unavailable and the shopper has no idea why.
     */
    cbHandOnlyTags() {
      const grid = this.querySelector('.cb-swatch-grid');
      if (!grid) return;
      const side = (v) => (v || '').trim().charAt(0).toUpperCase();
      const picked = this.querySelector('.option-selector[data-option="Hand"] .js-option:checked');
      const want = picked ? side(picked.value) : '';
      grid.querySelectorAll('.opt-label--image, .cb-extra-swatch').forEach((card) => {
        const input = card.classList.contains('cb-extra-swatch') ? card : card.previousElementSibling;
        const hands = ((input && input.dataset ? input.dataset.cbHands : '') || '')
          .split('|').filter(Boolean).map(side);
        const off = card.classList.contains('cb-extra-swatch')
          ? card.classList.contains('is-unavailable-x')
          : (input && input.classList.contains('is-unavailable'));
        const show = !!want && off && hands.length > 0 && !hands.includes(want);
        let tag = card.querySelector('.cb-hand-alt');
        card.classList.toggle('cb-hand-only', show);
        if (!show) { if (tag) tag.remove(); return; }
        if (!tag) {
          tag = document.createElement('span');
          tag.className = 'cb-hand-alt';
          card.appendChild(tag);      // on the card itself — some cards render without a meta span
        }
        tag.textContent = (hands.includes('L') ? 'LHT' : 'RHT') + ' Only';
      });
    }

    /** The swatch that points at the glove this page IS (handle match: a collection-scoped URL
     *  like /collections/fielding-gloves/products/x still has to match /products/x). */
    cbSelfInput() {
      const here = window.location.pathname.split('/products/').pop().split('?')[0];
      if (!here) return null;
      return [...this.querySelectorAll('.cb-swatch-grid .js-option[data-product-url]')]
        .find((i) => (i.dataset.productUrl || '').split('?')[0].split('/products/').pop() === here) || null;
    }

    /**
     * CheapBats 2026-09-14 (Mike): someone arriving from a collection card or Google clicked THAT
     * glove — so its swatch starts selected and sits first in the row, instead of the shopper having
     * to find it among thirty. Checked without firing change: it is already the product on screen.
     */
    cbMarkSelf() {
      const self = this.cbSelfInput();
      if (!self) return;
      if (!this.querySelector('.cb-swatch-grid .js-option:checked')) self.checked = true;
      self.classList.add('cb-self');
      const label = self.nextElementSibling;
      if (label) label.classList.add('cb-self');
    }

    /**
     * CheapBats 2026-09-13 (Mike): size filter for single-parent series. The HOH pages carry a Size
     * row that NAVIGATES (one parent per size, forced by Shopify's 60-child cap); a series that fits
     * in one parent gets the same-looking row that FILTERS the Style grid in place. Chips are derived
     * from the swatches themselves, so they follow the nightly stock sync with no extra data.
     * Only worth it above 12 styles / 3 sizes — below that the whole grid is already on screen.
     */
    cbSizeFilter() {
      const grid = this.querySelector('.cb-swatch-grid[data-cb-sizefilter]');
      if (!grid || grid.dataset.cbSizeDone) return;
      const cards = [...grid.querySelectorAll('.opt-label--image, .cb-extra-swatch')];
      const side = (v) => (v || '').trim().charAt(0).toUpperCase();
      const sizeOf = (el) => {                       // 12.50" and 12.5" are one size
        const s = el.querySelector('.cb-swatch-size');
        if (!s) return '';
        const n = parseFloat(s.textContent.replace(/[^\d.]/g, ''));
        return isNaN(n) ? s.textContent.trim() : n + '"';
      };
      const posOf = (el) => {
        const p = el.querySelector('.cb-swatch-pos');
        return p ? p.textContent.trim() : '';
      };
      const groups = new Map();
      cards.forEach((c) => {
        const sz = sizeOf(c);
        if (!sz) return;
        if (!groups.has(sz)) groups.set(sz, { n: 0, pos: {} });
        const g = groups.get(sz);
        g.n += 1;
        const p = posOf(c);
        if (p) g.pos[p] = (g.pos[p] || 0) + 1;
      });
      if (groups.size < 3 || cards.length < 12) return;
      grid.dataset.cbSizeDone = '1';
      const num = (s) => parseFloat(String(s).replace(/[^\d.]/g, '')) || 0;
      const sizes = [...groups.keys()].sort((a, b) => num(a) - num(b));
      const popular = (grid.dataset.cbPopular || '').split('|').filter(Boolean);

      const fs = document.createElement('fieldset');
      fs.className = 'cb-series-sizes cb-size-filter';
      fs.innerHTML = '<legend class="label">Size</legend><div class="cb-series-sizes__btns"></div>';
      const row = fs.querySelector('.cb-series-sizes__btns');
      const chip = (label, val, sub, pop) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'opt-label opt-label--btn btn relative text-center cb-size-chip';
        b.dataset.cbSize = val;
        b.dataset.cbSub = sub || '';     // the chip's own caption (position, or "N styles")
        b.innerHTML = (pop ? '<span class="cb-size-pop">Popular</span>' : '')
          + '<span class="cb-size-main">' + label + '</span>'
          + (sub ? '<span class="cb-size-hint">' + sub + '</span>' : '');
        row.appendChild(b);
        return b;
      };
      sizes.forEach((sz) => {
        const g = groups.get(sz);
        const top = Object.entries(g.pos).sort((a, b) => b[1] - a[1])[0];
        chip(sz, sz, top ? top[0] : (g.n === 1 ? '1 style' : g.n + ' styles'), popular.includes(sz));
      });
      // Reads exactly like a HOH page: one size is always chosen. On a child page that is the size
      // of the glove being viewed; on the series page it's the best-selling size this series stocks,
      // else whichever size has the most styles.
      // The glove being viewed is the one whose swatch links to this very URL — Shopify doesn't
      // always mark it checked, and relying on :checked landed a 31.5" catcher's mitt on 11". 2026-09-13
      const self = this.cbSelfInput();
      const current = (self && self.nextElementSibling)
        || grid.querySelector('.js-option:checked + .opt-label--image, .opt-label--image.is-selected');
      // Mike 9/14: a size is NEVER preselected, on any page — the shopper picks it or it stays off.
      // The glove being viewed is already identified by its own swatch (selected + first), so a
      // preselected size chip only dims three quarters of the line without being asked.
      const landing = null;

      // sits between Hand and Style, exactly like the HOH size row (CSS order keeps it there)
      const styleFs = grid.closest('.option-selector') || grid;
      styleFs.parentNode.insertBefore(fs, styleFs);
      // Mike 9/13: the off-size styles are NOT hidden — they stay on the page greyed and sorted
      // after the chosen size (the page looks full, and you can see the rest of the line). Clicking
      // a greyed one switches the size row to it, the same way the greyed Hand chip behaves.
      const apply = ((want, scroll) => {
        grid.classList.toggle('cb-size-focus', !!want);
        row.querySelectorAll('.cb-size-chip').forEach((x) =>
          x.classList.toggle('is-active', !!want && x.dataset.cbSize === want));
        cards.forEach((c) => {
          const off = !!want && sizeOf(c) !== want;
          c.classList.toggle('cb-size-off', off);
          // Mike 9/13: a size chip that reads as selected must mean something in the grid — whether the
          // shopper tapped it or it was chosen for them on landing. Off-size styles dim and name their
          // size; they stay clickable, and clicking one moves the selection to it.
          let alt = c.querySelector('.cb-size-alt');
          if (off && !alt) {
            alt = document.createElement('span');
            alt.className = 'cb-size-alt';
            (c.querySelector('.cb-swatch-meta') || c).appendChild(alt);
          }
          if (alt) alt.textContent = off ? 'Available in ' + sizeOf(c) : '';
          const input = c.previousElementSibling;
          if (input && input.classList.contains('js-option')) input.classList.toggle('cb-size-off', off);
        });
        if (typeof this.cbReorderUnavailable === 'function') this.cbReorderUnavailable();
        if (scroll) grid.scrollLeft = 0;
        grid.dispatchEvent(new Event('scroll'));
        window.dispatchEvent(new Event('resize'));
        const act = row.querySelector('.cb-size-chip.is-active');
        if (act && scroll) act.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }).bind(this);
      row.addEventListener('click', (e) => {
        const b = e.target.closest('.cb-size-chip');
        if (!b) return;
        if (b.classList.contains('is-active')) { apply(null, true); return; }   // tap again = clear
        // A size with nothing in the current hand is still selectable: it switches to the hand that
        // does have it, then selects the size (Mike 9/13).
        if (b.classList.contains('cb-size-empty')) {
          const sel2 = this.querySelector('.option-selector[data-option="Hand"]');
          const picked = sel2 && sel2.querySelector('.js-option:checked');
          const want = picked ? side(picked.value) : '';
          const has = cards.some((c) => sizeOf(c) === b.dataset.cbSize
            && handOf(c).split('|').filter(Boolean).map(side).some((h) => h !== want));
          if (!has || !sel2) return;
          const swap = [...sel2.querySelectorAll('.js-option')].find((i) => side(i.value) !== want);
          if (!swap) return;
          swap.checked = true;
          swap.dispatchEvent(new Event('change', { bubbles: true }));
          setTimeout(() => apply(b.dataset.cbSize, true), 60);
          return;
        }
        apply(b.dataset.cbSize, true);
      });

      // Mike 9/13: a size chip must never offer a size that has nothing in the selected hand.
      // Recount per hand on every change; if the shopper's current size just emptied, move them to
      // one that actually has gloves.
      const handOf = (c) => {
        const i = c.classList.contains('cb-extra-swatch') ? c : c.previousElementSibling;
        return (i && i.dataset ? i.dataset.cbHands : '') || '';
      };
      const recount = () => {
        const picked = this.querySelector('.option-selector[data-option="Hand"] .js-option:checked');
        const want = picked ? side(picked.value) : '';
        const live = new Map(), other = new Map();     // per size: count in the picked hand / the other one
        cards.forEach((c) => {
          const sz = sizeOf(c);
          if (!sz) return;
          const hands = handOf(c).split('|').filter(Boolean).map(side);
          // no hand data (sibling not in last night's snapshot) counts for whichever hand is picked,
          // so it can never produce a chip that leads nowhere
          if (!want || !hands.length || hands.includes(want)) live.set(sz, (live.get(sz) || 0) + 1);
          if (want && hands.some((h) => h !== want)) other.set(sz, (other.get(sz) || 0) + 1);
        });
        row.querySelectorAll('.cb-size-chip').forEach((b) => {
          const n = live.get(b.dataset.cbSize) || 0;
          b.classList.toggle('cb-size-empty', want !== '' && n === 0);
          const hint = b.querySelector('.cb-size-hint');
          if (!hint || !want) return;
          // Mike 9/13: the chip keeps its own caption — position is what a shopper picks a size by.
          // The only time it changes is when that size has nothing in the hand they picked.
          if (n) { b.hidden = false; hint.textContent = b.dataset.cbSub || ''; return; }
          // Mike 9/13: say where that size DOES exist. A chip that leads nowhere is not shown at all.
          const elsewhere = other.get(b.dataset.cbSize) || 0;
          b.hidden = !elsewhere;
          if (elsewhere) hint.textContent = 'Available in ' + (want === 'R' ? 'LHT' : 'RHT');
        });
        const act = row.querySelector('.cb-size-chip.is-active');
        if (act && act.classList.contains('cb-size-empty')) {
          const next = [...row.querySelectorAll('.cb-size-chip')].find((b) => !b.classList.contains('cb-size-empty'));
          if (next) apply(next.dataset.cbSize, true);
        }
      };
      this.cbSizeRecount = recount;
      this.addEventListener('change', (e) => {
        if (e.target && e.target.classList && e.target.classList.contains('js-option')) setTimeout(recount, 0);
      });
      recount();
      // A greyed style is a normal, in-stock choice: one click selects it exactly like any other
      // swatch (no interception). The size row just follows along to the size that was picked.
      grid.addEventListener('click', (e) => {
        const card = e.target.closest('.opt-label--image, .cb-extra-swatch');
        if (!card) return;
        const sz = sizeOf(card);
        if (sz && sz !== row.querySelector('.cb-size-chip.is-active').dataset.cbSize) {
          setTimeout(() => apply(sz, false), 0);
        }
      });
      if (landing) apply(landing, false);
    }

    /**
     * CheapBats 2026-09-13 (Mike): the series swatch grid is a slider — give it prev/next arrows so it
     * reads as one. Arrows hide at either end; scroll snaps a page (80% of the visible width) at a time.
     */
    cbSliderArrows() {
      // Style grid + the Size row (Mike 9/13: Size is a 1-row slider too) get the same arrows.
      this.querySelectorAll('.cb-swatch-grid, .cb-series-sizes__btns').forEach((grid) => {
        if (grid.parentElement.classList.contains('cb-slider')) return;
        const wrap = document.createElement('div');
        wrap.className = 'cb-slider';
        grid.parentNode.insertBefore(wrap, grid);
        wrap.appendChild(grid);
        const mk = (dir) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'cb-slider__btn cb-slider__btn--' + dir;
          const what = grid.classList.contains('cb-series-sizes__btns') ? 'sizes' : 'styles';
          b.setAttribute('aria-label', (dir === 'prev' ? 'Previous ' : 'More ') + what);
          b.innerHTML = dir === 'prev'
            ? '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>'
            : '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
          b.addEventListener('click', () => grid.scrollBy({ left: (dir === 'prev' ? -1 : 1) * grid.clientWidth * 0.8, behavior: 'smooth' }));
          wrap.appendChild(b);
          return b;
        };
        const prev = mk('prev'), next = mk('next');
        // Mike 9/13: make "this scrolls sideways" unmissable — edge fades + a progress rail
        // that shows how much of the row is off-screen.
        const rail = document.createElement('div');
        rail.className = 'cb-slider__rail';
        rail.innerHTML = '<span class="cb-slider__thumb"></span>';
        wrap.appendChild(rail);
        const thumb = rail.firstElementChild;
        const update = () => {
          const max = grid.scrollWidth - grid.clientWidth - 2;
          prev.hidden = grid.scrollLeft <= 24;
          next.hidden = grid.scrollLeft >= max;
          wrap.classList.toggle('cb-slider--static', max <= 2);
          wrap.classList.toggle('cb-at-start', grid.scrollLeft <= 24);
          wrap.classList.toggle('cb-at-end', grid.scrollLeft >= max);
          const frac = Math.min(1, grid.clientWidth / grid.scrollWidth);
          thumb.style.width = (frac * 100) + '%';
          thumb.style.left = ((1 - frac) * (grid.scrollLeft / Math.max(1, max)) * 100) + '%';
        };
        grid.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        setTimeout(() => { if (grid.scrollLeft < 40) grid.scrollLeft = 0; update(); }, 50);
        setTimeout(update, 600);
      });
    }

    /** Overflow-twin link-swatches (cb-extra-swatch): grey by the selected Hand. */
    cbExtraAvailability(forceAll) {
      const handSel = this.querySelector('.option-selector[data-option="Hand"] .js-option:checked');
      this.querySelectorAll('.cb-extra-swatch').forEach((a) => {
        if (forceAll) { a.classList.add('is-unavailable-x'); return; }
        if (!handSel) { a.classList.remove('is-unavailable-x'); return; }
        const hands = (a.dataset.cbHands || '').split('|').filter(Boolean);
        a.classList.toggle('is-unavailable-x', hands.length > 0 && !hands.includes(handSel.value));
      });
    }

    /**
     * CheapBats 2026-09-13 (Mike, Amazon-style): greyed styles are never removed, but they sort
     * AFTER the available ones. Original order is kept in data-cb-idx so it restores exactly.
     */
    cbReorderUnavailable() {
      const cbRank = (el) => !el ? 2 : el.querySelector('.cb-best:not(.cb-deal)') ? 0 : el.querySelector('.cb-deal') ? 1 : 2;
      this.optionSelectors.forEach((selector) => {
        const wrap = selector.querySelector('.option-selector__btns');
        if (!wrap) return;
        const inputs = [...wrap.querySelectorAll('.js-option[data-product-url]')];
        const extras = [...wrap.querySelectorAll('.cb-extra-swatch')];
        if (!inputs.length && !extras.length) return;
        const pairs = inputs.map((input, k) => {
          if (input.dataset.cbIdx === undefined) input.dataset.cbIdx = String(k);
          const label = wrap.querySelector('label[for="' + input.id + '"]');
          const lab = label || input;
          return { input, label, idx: Number(input.dataset.cbIdx),
                   off: input.classList.contains('is-unavailable') ? 1 : 0,
                   sz: lab.classList.contains('cb-size-off') ? 1 : 0,
                   self: input.classList.contains('cb-self') ? 0 : 1, rank: cbRank(label) };
        }).concat(extras.map((a) => ({ input: a, label: null, idx: 10000 + Number(a.dataset.cbIdxX || 0),
                   off: a.classList.contains('is-unavailable-x') ? 1 : 0,
                   sz: a.classList.contains('cb-size-off') ? 1 : 0, self: 1, rank: cbRank(a) })));
        // Rule (Mike 9/13): styles you can actually buy in the selected HAND come first — a hand is a
        // hard constraint, a size is a preference. Then selected size, then Popular → Deal, then
        // catalog order. Hand beats size: picking LHT must surface the LHT gloves, whatever size.
        pairs.sort((a, b) => a.self - b.self || a.off - b.off || a.sz - b.sz || (a.off ? 0 : a.rank - b.rank) || a.idx - b.idx);
        const tail = [...wrap.querySelectorAll('[data-cb-hand-none], .cb-hand-hint')];
        pairs.forEach((p) => { wrap.appendChild(p.input); if (p.label) wrap.appendChild(p.label); });
        tail.forEach((t) => wrap.appendChild(t));
      });
    }

    /**
     * CheapBats 2026-09-13: a Hand chip for a hand this listing has NO glove in (but the family
     * stocks elsewhere) is clickable: it greys every style and shows the sizes that do have it.
     */
    cbBindHandNone() {
      this.querySelectorAll('[data-cb-hand-none]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const on = !btn.classList.contains('is-active');
          this.querySelectorAll('[data-cb-hand-none]').forEach((b) => b.classList.remove('is-active'));
          this.querySelectorAll('.cb-hand-hint').forEach((h) => { h.hidden = true; });
          this.querySelectorAll('.option-selector[data-option="Hand"] .js-option').forEach((i) => { i.checked = false; });
          if (on) {
            btn.classList.add('is-active');
            const hint = btn.nextElementSibling;
            if (hint && hint.classList.contains('cb-hand-hint')) hint.hidden = false;
            this.querySelectorAll('.js-option[data-product-url]').forEach((i) => i.classList.add('is-unavailable'));
            this.cbExtraAvailability(true);
            this.cbReorderUnavailable();
          } else {
            this.cbCrossAvailability();
          }
        });
      });
      this.addEventListener('change', (e) => {
        if (e.target.closest('.option-selector[data-option="Hand"]')) {
          this.querySelectorAll('[data-cb-hand-none]').forEach((b) => b.classList.remove('is-active'));
          this.querySelectorAll('.cb-hand-hint').forEach((h) => { h.hidden = true; });
        }
      });
    }

    /**
     * CheapBats 2026-09-01: the server renders each pill's price across ALL variants carrying
     * that value ("From $129.99" when colours differ). Once the shopper has picked the other
     * option(s), narrow it to the matching variants so a single-price combination reads as a
     * plain price. Prefers in-stock variants; falls back to all so sold-out pills keep a price.
     */
    cbRefreshPillPrice(input, i, val, variants, selected) {
      const label = input.labels && input.labels[0];
      if (!label) return;
      const priceEl = label.querySelector('.cb-opt-price');
      if (!priceEl || !variants[0] || typeof variants[0].p !== 'number') return;
      const matches = (v) => v.o[i] === val && v.o.every((o, j) => j === i || selected[j] === null || o === selected[j]);
      let pool = variants.filter((v) => matches(v) && v.a);
      if (!pool.length) pool = variants.filter(matches);
      if (!pool.length) return;
      const money = (c) => '$' + (c / 100).toFixed(2);
      const prices = pool.map((v) => v.p);
      const min = Math.min(...prices), max = Math.max(...prices);
      priceEl.textContent = min === max ? money(min) : 'From ' + money(min);
      const saving = Math.max(...pool.map((v) => (v.c > v.p ? v.c - v.p : 0)));
      let saveEl = label.querySelector('.cb-opt-save');
      if (saving >= 100) {
        if (!saveEl) { saveEl = document.createElement('span'); saveEl.className = 'cb-opt-save'; priceEl.after(saveEl); }
        saveEl.textContent = 'Save $' + Math.floor(saving / 100);
      } else if (saveEl) {
        saveEl.remove();
      }
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 250);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.cbPruneConflicts(evt.target.closest('.option-selector'));
      this.cbSwapMediaForPartial();
      const selectedOptionIds = this.getSelectedOptionIds();

      // Immediately update swatch label
      this.updateSwatchLabel();
      this.cbCrossAvailability();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = evt.target.dataset.productUrl || evt.detail?.productUrl;
      const variantId = evt.target.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          // CheapBats 2026-09-13: combined-listing swatch → keep the in-place swap but put the
          // CHILD product's URL + title in the address bar (Amazon-style), so shares/bookmarks/
          // analytics attribute to the glove actually shown. Sections don't return <title>, so
          // derive it from the new H1 (theme title pattern is "<product> | CheapBats.com").
          if (combinedProductUrl && !this.quickBuyContainer && !this.featuredProductContainer) {
            try {
              const cbUrl = new URL(combinedProductUrl, window.location.origin);
              window.history.replaceState({}, '', cbUrl.pathname);
              const cbH1 = this.section.querySelector('h1');
              if (cbH1) document.title = cbH1.textContent.trim() + ' | CheapBats.com';
              const cbCanon = document.querySelector('link[rel="canonical"]');
              if (cbCanon) cbCanon.href = cbUrl.origin + cbUrl.pathname;
            } catch (e) { /* non-fatal */ }
          }
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        this.cbCrossAvailability();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? input.value : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.section.querySelector('variant-picker [type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
